// src/pages/Banks.js
import { useState, useEffect, useContext } from "react";
import { Typography } from "@mui/material";
import axios from "axios";
import { useTranslation } from "react-i18next";
import Modal from "../../components/Modal";
import CustomButton from "../../components/CustomButton";
import CustomInput from "../../components/CustomInput";
import useLanguage from "../../utils/useLanguage";
import { useNavigate } from "react-router";
import nextIcon from "../../assets/next_icon.png";
import previousIcon from "../../assets/previous_icon.png";
import deleteIcon from "../../assets/delete_Icon.png";
import saveIcon from "../../assets/save_icon.png";
import closeIcon from "../../assets/close_icon.png";
import editIcon from "../../assets/edit_icon.png";
import addIcon from "../../assets/add_icon.png";
import ErrorBox from "../../components/ErrorBox";
import BackToDashboard from "../../components/BackToDashboard";
import { DataContext } from "../../utils/DataContext";
import Loader from "../../components/Loader";
import { UserAuth } from "../../utils/AuthContext";
import SectionDivider from "../../components/SectionDivider";
import InformationBox from "../../components/InformationBox";
import PageWrapper from "../../components/PageWrapper";

export default function BanksPage() {
  const { t } = useTranslation("general");
  const { currentDirection } = useLanguage();
  const [banks, setBanks] = useState([]);
  const [filteredBanks, setFilteredBanks] = useState([]);
  const [selectedBank, setSelectedBank] = useState(null);
  const [bankModal, setBankModal] = useState(false);
  const [loadingModal, setLoadingModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [bankFields, setBankFields] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [banksCurrentPage, setBanksCurrentPage] = useState(1);
  const [banksTotalPages, setBanksTotalPages] = useState(1);
  const [banksTotalRecords, setBanksTotalRecords] = useState(0);

  const { selectedSupplier, loading, setLoading } = useContext(DataContext);
  const { userData } = useContext(UserAuth);

  const navigate = useNavigate();

  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const organization = localStorage.getItem("username");
  const organizationId = localStorage.getItem("organizationId");

  useEffect(() => {
    const getAllBanks = async () => await getBanks();

    getAllBanks();
  }, [banksCurrentPage, searchTerm]);

  const getBanks = async () => {
    const token =
      localStorage.getItem("SuperAdminToken") ||
      localStorage.getItem("adminToken") ||
      localStorage.getItem("token");

    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_URL}/api/bank`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            //    "User-Id": organizationId,
            "organization-id": organizationId,
          },
          params: {
            page: banksCurrentPage,
            limit: 5,
            searchQuery: searchTerm,
            isIraqi: false,
          },
        }
      );

      if (response.data && response.data.banks) {
        setBanks(response.data.banks);
        setFilteredBanks(response.data.banks);
        setBanksTotalPages(response.data.totalPages);
        setBanksTotalRecords(response.data.totalRecords);
      } else {
        console.error("Failed to get the banks");
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error("Failed to get the banks", error);
    }
  };

  const openBankModal = (bank) => {
    setSelectedBank(bank);
    setBankFields({
      bankName: bank.name,
      bankNameWithNoSpace: bank.nameWithNoSpace,
      branches: bank.branch || [],
    });
    setIsEditing(false);
    setBankModal(true);
  };

  const handleInputChange = (e, fieldName) => {
    const value = e.target.value;
    setBankFields({
      ...bankFields,
      [fieldName]: value,
    });
  };

  const handleBranchChange = (index, field, value) => {
    const updatedBranches = [...bankFields.branches];
    updatedBranches[index] = {
      ...updatedBranches[index],
      [field]: value,
    };
    setBankFields({
      ...bankFields,
      branches: updatedBranches,
    });
  };

  const addBranch = () => {
    setBankFields({
      ...bankFields,
      branches: [
        ...bankFields.branches,
        { name: "", address: "", bankId: selectedBank?.id },
      ],
    });
  };

  const removeBranch = (index) => {
    const updatedBranches = bankFields.branches.filter((_, i) => i !== index);
    setBankFields({
      ...bankFields,
      branches: updatedBranches,
    });
  };

  const handleSaveBank = async () => {
    const token =
      localStorage.getItem("SuperAdminToken") ||
      localStorage.getItem("adminToken") ||
      localStorage.getItem("token");

    try {
      setLoadingModal(true);
      const response = await axios.put(
        `${process.env.REACT_APP_URL}/api/bank/${selectedBank?.id}`,
        {
          name: bankFields.bankName,
          nameWithNoSpace: bankFields.bankNameWithNoSpace,
          branches: bankFields.branches,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200) {
        setBankModal(false);
      } else {
        console.error("Failed to update bank:", response.data.message);
      }
      setLoadingModal(false);
    } catch (error) {
      console.error("Error updating bank:", error);
      setLoadingModal(false);
    } finally {
      await getBanks();
    }
  };

  const handleCancel = () => {
    setRemoveBankModal(false);
    setBankModal(false);
    setSelectedBank(null);
    setLoadingModal(false);
  };

  const openRemoveBankModal = (bank) => {
    setSelectedBank(bank);
    setRemoveBankModal(true);
  };

  const [showRemoveBankModal, setRemoveBankModal] = useState(false);

  const handleDeleteBank = async () => {
    const token = localStorage.getItem("token");
    setLoadingModal(true);
    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_URL}/api/bank/${selectedBank.id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200) {
        setRemoveBankModal(false);
        setBankModal(false);
      } else {
        console.error("Failed to delete bank:", response.data.message);
      }
    } catch (error) {
      console.error("Error deleting bank:", error);
    } finally {
      setLoadingModal(false);
      setBankModal(false);
      await getBanks();
    }
  };

  const [message, setMessage] = useState("");
  const [addBankModal, setAddBankModal] = useState(false);
  const [newBankFields, setNewBankFields] = useState({
    bankName: "",
    bankNameWithNoSpace: "",
    branches: [{ name: "", address: "" }],
  });

  const openAddBankModal = () => {
    setAddBankModal(true);
    setNewBankFields({
      bankName: "",
      bankNameWithNoSpace: "",
      branches: [{ name: "", address: "" }],
    });
  };

  const handleNewBankInputChange = (e, fieldName) => {
    const value = e.target.value;
    setNewBankFields((prevFields) => ({
      ...prevFields,
      [fieldName]: value,
      ...(fieldName === "bankName" && {
        bankNameWithNoSpace: value.replace(/\s+/g, ""),
      }),
    }));
  };

  const handleNewBranchChange = (index, field, value) => {
    const updatedBranches = [...newBankFields.branches];
    updatedBranches[index] = {
      ...updatedBranches[index],
      [field]: value,
    };
    setNewBankFields({
      ...newBankFields,
      branches: updatedBranches,
    });
  };

  const addNewBranch = () => {
    setNewBankFields({
      ...newBankFields,
      branches: [...newBankFields.branches, { name: "", address: "" }],
    });
  };

  const removeNewBranch = (index) => {
    const updatedBranches = newBankFields.branches.filter(
      (_, i) => i !== index
    );
    setNewBankFields({
      ...newBankFields,
      branches: updatedBranches,
    });
  };

  const handleCreateBank = async (e) => {
    e.preventDefault();

    try {
      // Empty fields validation
      if (!newBankFields.bankName || !newBankFields.bankNameWithNoSpace) {
        setError(true);
        setErrorMessage(t("emptyInputFields"));
        return;
      }

      // Validate branches
      const validBranches = newBankFields.branches.filter(
        (branch) => branch.name && branch.address
      );

      if (validBranches.length === 0) {
        setError(true);
        setErrorMessage(
          "At least one branch with name and address is required"
        );
        return;
      }

      setLoadingModal(true);
      const response = await axios.post(
        `${process.env.REACT_APP_URL}/createBank`,
        {
          name: newBankFields.bankName,
          nameWithNoSpace: newBankFields.bankNameWithNoSpace,
          branches: validBranches,
        }
      );

      setMessage(response.data.message);
      if (response.data.message === "Bank created successfully") {
        setError(false);
        setErrorMessage("");
        setAddBankModal(false);
      }
      setError(false);
      setErrorMessage("");
      setAddBankModal(false);
      setLoading(false);
    } catch (error) {
      if (error.response && error.response.data && error.response.data.error) {
        const errorKey = error.response.data.error;
        // Map backend errors to user-friendly messages
        switch (errorKey) {
          case "bankAlreadyExists":
            setError(true);
            setErrorMessage("Bank already exists");
            break;
          default:
            setError(true);
            setErrorMessage(t("unexpectedError"));
            break;
        }
      } else {
        console.error("Network or other error:", error.message);
        setError(true);
        setErrorMessage(t("networkError"));
      }
    } finally {
      setLoadingModal(false);
      await getBanks();
    }
  };

  const handleCancelAddBank = () => {
    setError(false);
    setErrorMessage("");
    setAddBankModal(false);
  };

  return (
    <div className="e-container">
      <PageWrapper
          style={{ direction: currentDirection }}


      >


        <div className="users-title-section">
          <h4 className="page--title">
            {t("banks", { defaultValue: "Banks" })}
          </h4>

          {!loading && userData.userType === "organization" ? (
            <CustomButton
              title={t("createBank")}
              variant="blank"
              icon={addIcon}
              iconSize={20}
              onClick={openAddBankModal}
            />
          ) : null}
        </div>

        {loading ? (
          <Loader />
        ) : (
          <>
            <CustomInput
              name="bankName"
              value={searchTerm}
              placeholder={searchTerm ? "" : t("search")}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {filteredBanks.length === 0 ? (
              <InformationBox>
                <p>{t("noBanks")}</p>
              </InformationBox>
            ) : (
              <>
                <ul
                  className="users-list"
                  style={{ direction: currentDirection }}
                >
                  {filteredBanks.map((bank) => (
                    <li
                      className="user-list-item"
                      key={bank.id}
                      onClick={() => openBankModal(bank)}
                      style={{ cursor: "pointer" }}
                    >
                      <p className="user-username">
                        {bank.name || "No Name Available"}
                      </p>
                      <p className="user-details">
                        {bank.branch?.length || 0}{" "}
                        {t("branches", { defaultValue: "branches" })}
                      </p>
                    </li>
                  ))}
                </ul>

                <section className="pagination">
                  <div className="pagination-buttons">
                    <CustomButton
                      variant="blank"
                      title={t("previous")}
                      onClick={() => setBanksCurrentPage(banksCurrentPage - 1)}
                      disabled={banksCurrentPage === 1}
                      icon={
                        currentDirection === "ltr" ? previousIcon : nextIcon
                      }
                      iconSize={15}
                    />
                    <span className="pagination-location">
                      {t("page")}: {banksCurrentPage} - {banksTotalPages}
                    </span>
                    <CustomButton
                      title={t("next")}
                      variant="blank"
                      onClick={() => setBanksCurrentPage(banksCurrentPage + 1)}
                      disabled={banksCurrentPage === banksTotalPages}
                      icon={
                        currentDirection === "ltr" ? nextIcon : previousIcon
                      }
                      iconSize={15}
                      reverseIcon
                    />
                  </div>

                  <div className="pagination-buttons">
                    <span className="pagination-location">
                      {t("totalNumber")}: {banksTotalRecords}
                    </span>
                  </div>
                </section>
              </>
            )}
          </>
        )}

        {bankModal && (
          <Modal
            title={isEditing ? t("editBank") : t("bank")}
            onClose={handleCancel}
            footer={
              <>
                {loadingModal ? <Loader /> : null}
                <section className="edit-user-actions">
                  {isEditing ? (
                    <>
                      <CustomButton
                        title={t("confirm")}
                        onClick={handleSaveBank}
                        icon={saveIcon}
                        iconSize={16}
                        disabled={loadingModal}
                      />
                      <CustomButton
                        title={t("cancel")}
                        onClick={handleCancel}
                        variant="blank"
                        icon={closeIcon}
                        iconSize={14}
                      />
                    </>
                  ) : (
                    <>
                      <CustomButton
                        variant="blank"
                        title={t("edit")}
                        onClick={() => setIsEditing(true)}
                        icon={editIcon}
                        iconSize={18}
                      />
                      <CustomButton
                        title={t("delete")}
                        variant="red"
                        icon={deleteIcon}
                        iconSize={18}
                        onClick={() => openRemoveBankModal(selectedBank)}
                      />
                    </>
                  )}
                </section>
              </>
            }
          >
            <div>
              <section>
                <div>
                  {isEditing ? (
                    <div className="edit-user-entity">
                      <span>
                        {t("bankName", { defaultValue: "Bank Name" })}:{" "}
                      </span>
                      <CustomInput
                        name="bankName"
                        value={bankFields.bankName || ""}
                        onChange={(e) => handleInputChange(e, "bankName")}
                      />
                    </div>
                  ) : (
                    <div className="view-user-entity">
                      <p>{t("bankName", { defaultValue: "Bank Name" })}:</p>
                      <p>{selectedBank?.name}</p>
                    </div>
                  )}
                </div>
              </section>
              <section>
                <div>
                  {isEditing ? (
                    <div>
                      <div className="edit-user-entity">
                        <SectionDivider title={"branches"} />
                        <div>
                          {bankFields.branches?.map((branch, index) => (
                            <div
                              key={index}
                              className="u-margin--bottom_400 u-padding_400 u-border_1_gray"
                            >
                              <div className="edit-user-entity">
                                <span>
                                  {t("branchName", {
                                    defaultValue: "Branch Name",
                                  })}
                                  :{" "}
                                </span>
                                <CustomInput
                                  value={branch.name || ""}
                                  onChange={(e) =>
                                    handleBranchChange(
                                      index,
                                      "name",
                                      e.target.value
                                    )
                                  }
                                />
                              </div>
                              <div className="edit-user-entity">
                                <span>
                                  {t("address", { defaultValue: "Address" })}:{" "}
                                </span>
                                <CustomInput
                                  value={branch.address || ""}
                                  onChange={(e) =>
                                    handleBranchChange(
                                      index,
                                      "address",
                                      e.target.value
                                    )
                                  }
                                />
                              </div>
                              <div className="o-row o-row--horizontal-align_end">
                                <CustomButton
                                  title={t("removeBranch")}
                                  variant="red"
                                  icon={deleteIcon}
                                  onClick={() => removeBranch(index)}
                                  iconSize={14}
                                />
                              </div>
                            </div>
                          ))}
                          <div className="o-row o-row--horizontal-align_end">
                            <CustomButton
                              title={t("addBranch", {
                                defaultValue: "Add Branch",
                              })}
                              variant="blank"
                              onClick={addBranch}
                              icon={addIcon}
                              iconSize={16}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="">
                      <SectionDivider title={"branches"} />
                      <div className="o-column o-column--gap-size_400">
                        {selectedBank?.branch?.map((branch, index) => (
                          <div>
                            <div className="view-user-entity">
                              <p>{t("branchName")}: </p>
                              <p>{branch.name}</p>
                            </div>
                            <div className="view-user-entity">
                              <p>{t("branchAddress")}: </p>
                              <p>{branch.address}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </section>
            </div>
          </Modal>
        )}

        {showRemoveBankModal && (
          <Modal
            title={t("deleteBank")}
            onClose={() => setRemoveBankModal(false)}
            footer={
              <>
                {loadingModal ? <Loader /> : null}
                <section className="delete-user-actions">
                  <CustomButton
                    onClick={handleDeleteBank}
                    title={t("confirm")}
                    icon={saveIcon}
                    iconSize={16}
                    disabled={loadingModal}
                  />
                  <CustomButton
                    title={t("cancel")}
                    onClick={() => setRemoveBankModal(false)}
                    variant="blank"
                    icon={closeIcon}
                    iconSize={14}
                  />
                </section>
              </>
            }
          >
            <div className="delete-modal-body">
              <h3>{t("areYouSureYouWantToDeleteThisBank")}</h3>
              <p>{selectedBank?.name}</p>
            </div>
          </Modal>
        )}

        {/* Add Bank Modal */}
        {addBankModal && (
          <Modal
            title={t("createBank", { defaultValue: "Create Bank" })}
            onClose={handleCancelAddBank}
            footer={
              <>
                {loadingModal ? <Loader /> : null}
                <section className="edit-user-actions">
                  <CustomButton
                    title={t("confirm")}
                    onClick={handleCreateBank}
                    icon={saveIcon}
                    iconSize={16}
                    disabled={loadingModal}
                  />
                  <CustomButton
                    title={t("cancel")}
                    onClick={handleCancelAddBank}
                    variant="blank"
                    icon={closeIcon}
                    iconSize={14}
                  />
                </section>
              </>
            }
          >
            <form onSubmit={handleCreateBank}>
              <div>
                {error && (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <ErrorBox>{errorMessage}</ErrorBox>
                  </div>
                )}
                <div className="edit-user-entity">
                  <span>{t("bankName", { defaultValue: "Bank Name" })}: </span>
                  <CustomInput
                    name="bankName"
                    value={newBankFields.bankName}
                    onChange={(e) => handleNewBankInputChange(e, "bankName")}
                    required
                  />
                </div>

                <div className="edit-user-entity">
                  <span>{t("branches", { defaultValue: "Branches" })}: </span>
                  <div>
                    {newBankFields.branches.map((branch, index) => (
                      <div key={index}>
                        <div className="edit-user-entity">
                          <span>
                            {t("branchName", { defaultValue: "Branch Name" })}:{" "}
                          </span>
                          <CustomInput
                            value={branch.name}
                            onChange={(e) =>
                              handleNewBranchChange(
                                index,
                                "name",
                                e.target.value
                              )
                            }
                          />
                        </div>
                        <div className="edit-user-entity">
                          <span>
                            {t("address", { defaultValue: "Address" })}:{" "}
                          </span>
                          <CustomInput
                            value={branch.address}
                            onChange={(e) =>
                              handleNewBranchChange(
                                index,
                                "address",
                                e.target.value
                              )
                            }
                          />
                        </div>
                        {newBankFields.branches.length > 1 && (
                          <CustomButton
                            title={t("removeBranch")}
                            variant="red"
                            icon={deleteIcon}
                            onClick={() => removeNewBranch(index)}
                            iconSize={14}
                          />
                        )}
                      </div>
                    ))}
                    <div className="o-row o-row--horizontal-align_end">
                      <CustomButton
                        title={t("addBranch", { defaultValue: "Add Branch" })}
                        variant="blank"
                        onClick={addNewBranch}
                        icon={addIcon}
                        iconSize={16}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </Modal>
        )}
      </PageWrapper>

    </div>
  );
}
